
import React from 'react';
import {
  FormItem,
  FormLabel,
  FormControl,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { MemberPermission } from '../../types/memberTypes';

interface PermissionsDropdownProps {
  permissions: MemberPermission[];
  setFormField: (field: string, value: any) => void;
}

const PermissionsDropdown: React.FC<PermissionsDropdownProps> = ({
  permissions,
  setFormField
}) => {
  // Group permissions by type
  const portfolioPermissions = permissions?.filter(p => 
    ['portfolioUsers', 'portfolioSettings', 'viewOnly'].includes(p.id)
  );
  
  const businessPermissions = permissions?.filter(p => 
    ['campaigns', 'budget', 'billing', 'leads', 'businessUsers'].includes(p.id)
  );

  // Calculate checked permissions count
  const getCheckedPermissionsCount = (permissions?: MemberPermission[]) => {
    if (!permissions) return 0;
    return permissions.filter(p => p.checked).length;
  };

  const handlePermissionChange = (permissionId: string, checked: boolean) => {
    // Find the viewOnly permission
    const viewOnlyPermission = permissions.find(p => p.id === 'viewOnly');
    const isViewOnly = permissionId === 'viewOnly';
    
    // Create a copy of the current permissions
    let updatedPermissions = [...permissions];
    
    if (isViewOnly && checked) {
      // If viewOnly is being checked, uncheck all other permissions
      updatedPermissions = updatedPermissions.map(p => ({
        ...p,
        checked: p.id === 'viewOnly'
      }));
    } else if (!isViewOnly && checked) {
      // If any other permission is being checked, uncheck viewOnly
      updatedPermissions = updatedPermissions.map(p => ({
        ...p,
        checked: p.id === 'viewOnly' ? false : (p.id === permissionId ? checked : p.checked)
      }));
    } else if (!isViewOnly && !checked) {
      // If a non-viewOnly permission is being unchecked, check if all others are unchecked
      updatedPermissions = updatedPermissions.map(p => ({
        ...p,
        checked: p.id === permissionId ? checked : p.checked
      }));
      
      // Check if all non-viewOnly permissions are now unchecked
      const anyNonViewOnlyChecked = updatedPermissions.some(p => 
        p.id !== 'viewOnly' && p.checked
      );
      
      if (!anyNonViewOnlyChecked) {
        // If no other permissions are checked, automatically check viewOnly
        updatedPermissions = updatedPermissions.map(p => ({
          ...p,
          checked: p.id === 'viewOnly' ? true : p.checked
        }));
      }
    }
    
    // Update the form state with the new permissions
    setFormField('permissions', updatedPermissions);
  };

  return (
    <div>
      <FormItem>
        <FormLabel>Permissions</FormLabel>
        <FormControl>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-full justify-between">
                Permissions
                <span className="ml-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs">
                  {getCheckedPermissionsCount(permissions)}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent 
              className="w-56 bg-white"
              align="start"
              sideOffset={4}
            >
              {/* Portfolio Permissions Group */}
              <DropdownMenuLabel>This Portfolio</DropdownMenuLabel>
              {portfolioPermissions.map((permission) => (
                <DropdownMenuCheckboxItem
                  key={permission.id}
                  checked={permission.checked}
                  onSelect={(e) => e.preventDefault()}
                  onCheckedChange={(checked) => handlePermissionChange(permission.id, Boolean(checked))}
                >
                  {permission.name}
                </DropdownMenuCheckboxItem>
              ))}
              
              <DropdownMenuSeparator />
              
              {/* Business Permissions Group */}
              <DropdownMenuLabel>Managed Businesses</DropdownMenuLabel>
              {businessPermissions.map((permission) => (
                <DropdownMenuCheckboxItem
                  key={permission.id}
                  checked={permission.checked}
                  onSelect={(e) => e.preventDefault()}
                  onCheckedChange={(checked) => handlePermissionChange(permission.id, Boolean(checked))}
                >
                  {permission.name}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </FormControl>
      </FormItem>
    </div>
  );
};

export default PermissionsDropdown;
